import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import monitoringService from '../../services/monitoringService';
import DatePickerField from './DatePickerField';
import { formatCategory } from '../../pages/inspections/categoryOptions';
import '../../pages/monitoring/Monitoring.css';

const todayIso = () => new Date().toISOString().slice(0, 10);

const STATUS_LABELS = {
  pending: 'Pending',
  submitted: 'Submitted',
  verified: 'Verified',
  overdue: 'Overdue',
  due: 'Due',
  upcoming: 'Upcoming',
};

const emptySubmission = {
  entryDate: todayIso(),
  numberValue: '',
  textValue: '',
  booleanValue: 'true',
  selectValue: '',
  notes: '',
};

const TaskListView = ({
  title = 'Inspection Tasks',
  subtitle = 'Tasks assigned from monitoring configuration and EIA conditions',
  showHeader = true,
}) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTask, setActiveTask] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submission, setSubmission] = useState(emptySubmission);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const data = await monitoringService.getTasks(params);
      setTasks(data.tasks || []);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      toast.error(error.response?.data?.error || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const filteredTasks = tasks.filter((task) => {
    const q = searchQuery.toLowerCase();
    return !q
      || task.name.toLowerCase().includes(q)
      || (task.componentName || '').toLowerCase().includes(q)
      || (task.category || '').toLowerCase().includes(q);
  });

  const stats = {
    total: tasks.length,
    dueOrPending: tasks.filter((t) => t.status === 'pending' || t.status === 'due').length,
    overdue: tasks.filter((t) => t.status === 'overdue').length,
    completed: tasks.filter((t) => t.status === 'submitted' || t.status === 'verified').length,
  };

  const handleFieldChange = (field) => (e) => {
    const value = e.target ? e.target.value : e;
    setSubmission((prev) => ({ ...prev, [field]: value }));
  };

  const openSubmitModal = (task) => {
    setActiveTask(task);
    setSubmission(emptySubmission);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activeTask) return;

    try {
      setSubmitting(true);

      if (activeTask.source === 'parameter') {
        let value;
        switch (activeTask.dataType) {
          case 'number':
            if (submission.numberValue === '') {
              toast.error('Please enter a value');
              setSubmitting(false);
              return;
            }
            value = { number: parseFloat(submission.numberValue) };
            break;
          case 'boolean':
            value = { boolean: submission.booleanValue === 'true' };
            break;
          case 'select':
            if (!submission.selectValue) {
              toast.error('Please choose an option');
              setSubmitting(false);
              return;
            }
            value = { select: submission.selectValue };
            break;
          default:
            if (!submission.textValue.trim()) {
              toast.error('Please enter a value');
              setSubmitting(false);
              return;
            }
            value = { text: submission.textValue.trim() };
        }

        await monitoringService.submitEntry({
          parameterId: activeTask.parameterId,
          projectId: activeTask.projectId,
          entryDate: submission.entryDate,
          value,
          notes: submission.notes || null,
        });
      } else {
        await monitoringService.submitRecord(activeTask.taskId, {
          value: submission.textValue,
          notes: submission.notes,
          submittedAt: new Date().toISOString(),
        });
      }

      toast.success('Task submitted successfully');
      setActiveTask(null);
      fetchTasks();
    } catch (error) {
      console.error('Failed to submit task:', error);
      toast.error(error.response?.data?.error || 'Failed to submit task');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="monitoring-page">
      {showHeader && (
        <div className="monitoring-header">
          <div>
            <h1><i className="fas fa-clipboard-check"></i> {title}</h1>
            <p className="header-subtitle">{subtitle}</p>
          </div>
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon"><i className="fas fa-tasks"></i></div>
          <div><div className="stat-label">Total Tasks</div><div className="stat-value">{stats.total}</div></div>
        </div>
        <div className="stat-card warning">
          <div className="stat-icon"><i className="fas fa-clock"></i></div>
          <div><div className="stat-label">Due / Pending</div><div className="stat-value">{stats.dueOrPending}</div></div>
        </div>
        <div className="stat-card danger">
          <div className="stat-icon"><i className="fas fa-exclamation-circle"></i></div>
          <div><div className="stat-label">Overdue</div><div className="stat-value">{stats.overdue}</div></div>
        </div>
        <div className="stat-card success">
          <div className="stat-icon"><i className="fas fa-check-circle"></i></div>
          <div><div className="stat-label">Completed</div><div className="stat-value">{stats.completed}</div></div>
        </div>
      </div>

      <div className="monitoring-controls">
        <input
          className="form-control"
          style={{ minWidth: '260px' }}
          placeholder="Search tasks or projects..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="due">Due</option>
          <option value="pending">Pending</option>
          <option value="upcoming">Upcoming</option>
          <option value="submitted">Submitted</option>
          <option value="verified">Verified</option>
          <option value="overdue">Overdue</option>
        </select>
      </div>

      {loading ? (
        <div className="empty-state">
          <i className="fas fa-spinner fa-spin"></i>
          <h3>Loading tasks...</h3>
        </div>
      ) : filteredTasks.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-clipboard-list"></i>
          <h3>No tasks found</h3>
          <p>Tasks appear automatically once monitoring parameters are assigned or EIA conditions generate them.</p>
        </div>
      ) : (
        <div className="monitoring-list">
          {filteredTasks.map((task) => (
            <div key={task.id} className="monitoring-card">
              <div className="monitoring-card-main">
                <h4>{task.name}</h4>
                <div className="monitoring-card-meta">
                  <span><i className="fas fa-project-diagram"></i> {task.componentName || 'Project'}</span>
                  <span><i className="fas fa-calendar"></i> Due {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'now'}</span>
                  <span className="category-badge">{formatCategory(task.category)}</span>
                  {task.frequency && <span><i className="fas fa-redo"></i> {task.frequency.replace('_', ' ')}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span className={`status-badge status-${task.status}`}>{STATUS_LABELS[task.status] || task.status}</span>
                {task.status !== 'verified' && (
                  <button className="btn-primary-green" onClick={() => openSubmitModal(task)}>
                    <i className="fas fa-file-alt"></i> Submit
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTask && (
        <div className="modal-overlay" onClick={() => setActiveTask(null)}>
          <div className="monitoring-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-file-alt"></i> Submit: {activeTask.name}</h2>
              <button className="close-btn" onClick={() => setActiveTask(null)}><i className="fas fa-times"></i></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-content">
                <div className="form-grid">
                  {activeTask.source === 'parameter' && (
                    <div className="form-group full-width">
                      <label>Date *</label>
                      <DatePickerField value={submission.entryDate} onChange={handleFieldChange('entryDate')} maxDate={todayIso()} />
                    </div>
                  )}
                  {activeTask.source === 'parameter' && activeTask.dataType === 'number' && (
                    <div className="form-group full-width">
                      <label>Value {activeTask.unit ? `(${activeTask.unit})` : ''} *</label>
                      <input type="number" step="any" value={submission.numberValue} onChange={handleFieldChange('numberValue')} required />
                    </div>
                  )}
                  {activeTask.source === 'parameter' && activeTask.dataType === 'boolean' && (
                    <div className="form-group full-width">
                      <label>Value *</label>
                      <select value={submission.booleanValue} onChange={handleFieldChange('booleanValue')}>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                  )}
                  {activeTask.source === 'parameter' && activeTask.dataType === 'select' && (
                    <div className="form-group full-width">
                      <label>Value *</label>
                      <select value={submission.selectValue} onChange={handleFieldChange('selectValue')} required>
                        <option value="">Select...</option>
                        {(activeTask.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    </div>
                  )}
                  {(activeTask.source !== 'parameter' || activeTask.dataType === 'text') && (
                    <div className="form-group full-width">
                      <label>Measured value / observation *</label>
                      <input type="text" value={submission.textValue} onChange={handleFieldChange('textValue')} placeholder="Observation" required />
                    </div>
                  )}
                  <div className="form-group full-width">
                    <label>Notes</label>
                    <textarea rows="3" value={submission.notes} onChange={handleFieldChange('notes')} placeholder="Method, weather, additional context..." />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setActiveTask(null)}>Cancel</button>
                <button type="submit" className="btn-primary-green" disabled={submitting}>{submitting ? 'Submitting...' : 'Submit'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskListView;
